import _ from 'underscore';
import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import type { I18nLike } from '@bingads-webui/common-types';
import { Select } from '@bingads-webui-react/primitive-validation';
import { LineByLineFormGroup } from '@bingads-webui-cc-react/layout';

export interface Currency {
  Id?: number;
  Code: string;
  ControlledCurrencyCountryId?: number | null;
  DisplayName: string;
}

export interface CurrencyChangeData {
  currencyCode: string | null;
  isValid: boolean;
}

export interface CurrencySelectorProps {
  i18n: I18nLike;
  id: string;
  currencyCode?: string | null;
  currencyList: Currency[];
  onCurrencyChange: (currencyData: CurrencyChangeData) => void;
  helpPopupId?: string;
  helpPopupAriaLabel?: string;
  disabled?: boolean;
  isReadonly?: boolean;
  isOptional?: boolean;
  allowEmpty?: boolean;
}

type CurrencySelectorPropsWithDefaults = CurrencySelectorProps & Required<Pick<
  CurrencySelectorProps,
  'currencyCode' | 'helpPopupId' | 'helpPopupAriaLabel' | 'disabled' | 'isReadonly' | 'isOptional' | 'allowEmpty'
>>;

type SelectValidationProps = NonNullable<React.ComponentProps<typeof Select>['validationProps']>;
type SelectValidationResult = Parameters<NonNullable<SelectValidationProps['onValidate']>>[0];
type CurrencyValidationResult = Omit<SelectValidationResult, 'data'> & {
  data: {
    currencyCode?: string | null;
  };
};

export const CurrencySelector = ({
  i18n,
  id,
  currencyCode: currencyCodeProp,
  currencyList,
  onCurrencyChange,
  helpPopupId,
  helpPopupAriaLabel,
  disabled,
  isReadonly,
  isOptional,
  allowEmpty,
}: CurrencySelectorPropsWithDefaults) => {
  const onValidate = useCallback(({
    data,
    isValid,
  }: CurrencyValidationResult) => {
    // When allowEmpty is true, don't fallback to first currency when code is null/empty
    const fallbackCode: string | null | undefined = allowEmpty ? null : _.get(_.first(currencyList), 'Code');
    const submittedCurrencyCode = data.currencyCode;
    const resolvedCode = submittedCurrencyCode || fallbackCode;
    onCurrencyChange({ currencyCode: resolvedCode, isValid: resolvedCode ? isValid : false });
  }, [allowEmpty, currencyList, onCurrencyChange]);

  const currencyCode = useMemo(() => {
    if (currencyCodeProp) {
      if (_.find(currencyList, (c: Currency) => c.Code === currencyCodeProp)) {
        return currencyCodeProp;
      }
    }
    return null;
  }, [currencyCodeProp, currencyList]);

  return (
    <LineByLineFormGroup
      id={`${id}_CurrencyFormGroup`}
      label={i18n.getString(_TL_('Currency'))}
      helpPopupTopic={helpPopupId.length !== 0 ? helpPopupId : 'Page_Accounts_Details_Currency'}
      helpPopupAriaLabel={helpPopupAriaLabel}
    >
      {isReadonly && currencyCode}
      {!isReadonly &&
        <Select
          validationProps={{
            i18n,
            onValidate,
            field: 'currencyCode',
            data: { currencyCode },
            schema: {
              properties: {
                currencyCode: {
                  type: 'string',
                  required: !isOptional,
                },
              },
            },
          }}
          disabled={disabled}
          name="CurrencySelector"
          aria-label={i18n.getString(_TL_('Currency'))}
        >
          {allowEmpty && !currencyCode && <option value="">{i18n.getString(_TL_('Select a currency'))}</option>}
          {currencyList.map((c: Currency) => <option value={c.Code} key={c.Code}>{c.DisplayName}</option>)}
        </Select>}
    </LineByLineFormGroup>
  );
};

CurrencySelector.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  id: PropTypes.string.isRequired,
  currencyCode: PropTypes.string,
  currencyList: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  onCurrencyChange: PropTypes.func.isRequired,
  helpPopupId: PropTypes.string,
  helpPopupAriaLabel: PropTypes.string,
  disabled: PropTypes.bool,
  isReadonly: PropTypes.bool,
  isOptional: PropTypes.bool,
  allowEmpty: PropTypes.bool,
};

CurrencySelector.defaultProps = {
  currencyCode: '',
  helpPopupId: '',
  helpPopupAriaLabel: '',
  isReadonly: false,
  disabled: false,
  isOptional: false,
  allowEmpty: false,
};
